import { memo } from "react";

import { RadioGroup } from "@headlessui/react";
import { parseGid } from "@shopify/hydrogen-react";

import { Heading } from "@react/components/Heading";
import { Text } from "@react/components/Text";
import { findSelectedOption, isGenericOption } from "@react/utilities";
import {
  type SelectedOption,
  type CartProductVariant,
  type CartRenderVariantOption,
} from "@tablescape/state";
import { type TTailwindString, tw } from "classnames";

const styles = {
  header: {
    container: tw("flex"),
    details: tw("flex", "items-center", "gap-2", "flex-1", "text-primary"),
    detailsText: tw("text-primary-muted"),
    heading: tw("mt-1"),
  },
  variants: {
    container: tw(
      "m-0",
      "p-0",
      "list-none",
      "flex",
      "gap-[10px]" as TTailwindString,
      "flex-row",
      "flex-wrap"
    ),
    option: (option: SelectedOption | undefined) =>
      tw({
        "flex-1": option ? isGenericOption(option) : false,
      }),
  },
  footer: {
    shippingNote: tw("text-special", "mt-2"),
  },
};

type Props = {
  context?: "productForm" | "productSelector" | "cart";
  name: string;
  productName?: string;
  renderOption: CartRenderVariantOption;
  selected: CartProductVariant | undefined;
  showProductName?: boolean;
  type: "artwork" | "color" | "generic" | "style";
  value: string;
  variants: CartProductVariant[];
};

const OptionSelectorBase = ({
  context,
  name,
  productName,
  renderOption,
  selected,
  showProductName,
  type,
  value,
  variants,
}: Props) => {
  return (
    <>
      <RadioGroup.Description as="div" className={styles.header.container}>
        <div className={styles.header.details}>
          <Heading as="h6" className={styles.header.heading}>
            {showProductName ? productName : name}:
          </Heading>
          {(!selected?.optionCategory ||
            name === selected?.optionCategory ||
            context !== "productForm") && (
            <div className={styles.header.detailsText}>
              <Text as="body2">{value}</Text>
            </div>
          )}
        </div>
      </RadioGroup.Description>
      <div className={styles.variants.container}>
        {variants
          .filter(
            (variant) =>
              !variant.optionCategory ||
              context !== "productForm" ||
              variant.optionCategory === name ||
              showProductName
          )
          .map((variant, index) => {
            const selectedOption = findSelectedOption({
              type,
              variant,
            });

            return (
              <RadioGroup.Option
                className={styles.variants.option(selectedOption)}
                data-mtr-as="clickProductSwatch"
                data-mtr-position={index + 1}
                data-mtr-product-variant-id={parseGid(variant.id).id}
                data-mtr-product-variant-name={variant.title}
                data-mtr-product-variant-price={variant.price?.amount}
                data-mtr-product-variant-sku={variant.sku}
                data-mtr-target="swatch"
                data-mtr-track="click"
                key={variant.id}
                value={variant.id}
              >
                {({ active, checked }) => {
                  return (
                    <>
                      <RadioGroup.Label>
                        {renderOption({
                          active,
                          checked,
                          currentOption: selectedOption,
                          currentVariant: variant,
                        })}
                      </RadioGroup.Label>
                    </>
                  );
                }}
              </RadioGroup.Option>
            );
          })}
      </div>

      {context === "productForm" &&
        selected?.optionCategory === name &&
        selected?.shippingNote && (
          <RadioGroup.Description
            as="div"
            className={styles.footer.shippingNote}
          >
            <Text as="body2" size="sm">
              {selected.shippingNote}
            </Text>
          </RadioGroup.Description>
        )}
    </>
  );
};

export const OptionSelector = memo(OptionSelectorBase);
